import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';
import hidevLogo from '../assets/hidev_logo.png';

interface NavLink {
  label: string;
  href: string;
}

interface FooterProps {
  className?: string;
  showPrivacyPolicy?: boolean;
  privacyPolicyText?: string;
  showTermsAndConditions?: boolean;
  termsAndConditionsText?: string;
}

const Footer: React.FC<FooterProps> = ({
  className = '',
  showPrivacyPolicy = false,
  privacyPolicyText = '',
  showTermsAndConditions = false,
  termsAndConditionsText = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const navLinks: NavLink[] = [
    { label: t('home'), href: '/' },
    { label: t('about_us'), href: '/#about' },
    { label: t('services'), href: '/#services' },
    { label: t('contact'), href: '/#contact' },
    { label: t('locations'), href: '/our-locations' },
  ];

  // Handle smooth scroll navigation
  const handleNavigation = (href: string) => {
    if (typeof window === 'undefined') return;

    if (href.startsWith('/#')) {
      const sectionId = href.slice(2);
      
      // If we're not on the home page, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation, then scroll
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.offsetTop - headerOffset;
            window.scrollTo({ top: elementPosition, behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.offsetTop - headerOffset;
          window.scrollTo({ top: elementPosition, behavior: 'smooth' });
        }
      }
    } else if (href === '/') {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <footer className={`${className}`} style={{ 
        backgroundColor: 'var(--pure-white)',
        borderTop: '1px solid var(--light-gray)'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '2rem 1rem'
        }}>

          {/* Desktop Layout */}
          <div className="footer-desktop" style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Logo and Business Name */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Link to="/" style={{ flexShrink: 0 }}>
                <img
                  src={logo}
                  alt="Airline Crew Trans"
                  style={{ height: '48px', width: 'auto', objectFit: 'contain' }}
                />
              </Link>
              <span style={{
                marginLeft: '0.75rem',
                color: 'var(--rich-black)',
                fontWeight: 600,
                fontFamily: 'var(--font-secondary)'
              }}>
                Airline Crew Trans
              </span>
            </div>

            {/* Navigation Links */}
            <nav style={{ display: 'flex', gap: '2rem' }}>
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigation(link.href)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--dark-gray)',
                    fontWeight: 500,
                    fontFamily: 'var(--font-secondary)',
                    fontSize: '0.9375rem',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    padding: 0
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rich-black)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dark-gray)'}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* HiDev Logo - Right */}
            <div style={{ flexShrink: 0, width: '100px' }}>
              <a
                href="https://hidevmobile.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', width: '100px', transition: 'all 0.3s ease' }}
              >
                <img
                  src={hidevLogo}
                  alt="HiDev"
                  style={{
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                  }}
                />
              </a>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="footer-mobile" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            {/* Logo and Business Name */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Link to="/">
                <img
                  src={logo}
                  alt="Airline Crew Trans"
                  style={{ height: '48px', width: 'auto', objectFit: 'contain' }}
                />
              </Link>
              <span style={{
                color: 'var(--rich-black)',
                fontWeight: 600,
                textAlign: 'center',
                fontFamily: 'var(--font-secondary)'
              }}>
                Airline Crew Trans
              </span>
            </div>

            {/* Navigation Links */}
            <nav style={{
              textAlign: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '0.75rem 1.5rem'
            }}>
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigation(link.href)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--dark-gray)',
                    fontWeight: 500,
                    fontFamily: 'var(--font-secondary)',
                    fontSize: '0.9375rem',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    padding: 0
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rich-black)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dark-gray)'}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* HiDev Logo */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <a
                href="https://hidevmobile.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', width: '90px', transition: 'all 0.3s ease' }}
              >
                <img
                  src={hidevLogo}
                  alt="HiDev"
                  style={{
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                  }}
                />
              </a>
            </div>
          </div>

          {/* Copyright and Legal Links */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--light-gray)'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              {/* Legal Links */}
              {(showPrivacyPolicy || showTermsAndConditions) && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  {showPrivacyPolicy && (
                    <button
                      onClick={() => setPrivacyModalOpen(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--dark-gray)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                        fontFamily: 'var(--font-secondary)',
                        padding: 0
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rich-black)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dark-gray)'}
                    >
                      Privacy Policy
                    </button>
                  )}
                  {showPrivacyPolicy && showTermsAndConditions && (
                    <span style={{ color: 'var(--medium-gray)' }}>|</span>
                  )}
                  {showTermsAndConditions && (
                    <button
                      onClick={() => setTermsModalOpen(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--dark-gray)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                        fontFamily: 'var(--font-secondary)',
                        padding: 0
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rich-black)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dark-gray)'}
                    >
                      Terms and Conditions
                    </button>
                  )}
                </div>
              )}

              {/* Copyright */}
              <div style={{
                textAlign: 'center',
                fontSize: '0.875rem',
                color: 'var(--dark-gray)',
                fontFamily: 'var(--font-secondary)'
              }}>
                © {year} {t('footer_copyright')}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {privacyModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}>
          <div style={{
            backgroundColor: 'var(--pure-white)',
            borderRadius: '8px',
            maxWidth: '672px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                fontFamily: 'var(--font-secondary)',
                color: 'var(--rich-black)'
              }}>
                Privacy Policy
              </h2>
              <button
                onClick={() => setPrivacyModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--dark-gray)',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'var(--dark-gray)',
                fontFamily: 'var(--font-secondary)'
              }}
              dangerouslySetInnerHTML={{ __html: privacyPolicyText }}
            />
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {termsModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}>
          <div style={{
            backgroundColor: 'var(--pure-white)',
            borderRadius: '8px',
            maxWidth: '672px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                fontFamily: 'var(--font-secondary)',
                color: 'var(--rich-black)'
              }}>
                Terms and Conditions
              </h2>
              <button
                onClick={() => setTermsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--dark-gray)',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'var(--dark-gray)',
                fontFamily: 'var(--font-secondary)'
              }}
              dangerouslySetInnerHTML={{ __html: termsAndConditionsText }}
            />
          </div>
        </div>
      )}

      {/* Responsive Styles */}
      <style>{`
        @media (min-width: 1024px) {
          .footer-desktop {
            display: flex !important;
          }
          .footer-mobile {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Footer;
